import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  });

  useEffect(() => {
    // Check for impersonation token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate_token');
    if (impersonateToken) {
      sessionStorage.setItem('token', impersonateToken);
      sessionStorage.setItem('impersonating', 'true');
      setToken(impersonateToken);
      window.history.replaceState({}, '', window.location.pathname);
      fetchUser();
      return;
    }

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!currentToken) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(response.data);
      setToken(currentToken);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      // Normalize email: trim, lowercase, strip diacritics/accents (e.g. seymá → seyma)
      const normalizedEmail = email.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const response = await axios.post(`${API_URL}/auth/login`, { 
        email: normalizedEmail, 
        password,
        remember_me: rememberMe 
      });
      const { token: newToken, user: userData } = response.data;
      
      // Store token based on remember_me preference
      if (rememberMe) {
        localStorage.setItem('token', newToken);
        sessionStorage.removeItem('token');
      } else {
        sessionStorage.setItem('token', newToken);
        localStorage.removeItem('token');
      }
      
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      // Check _originalKey (set by AxiosErrorInterceptor) first, then raw detail as fallback
      const originalKey = error.response?.data?._originalKey 
        || error.response?.data?.detail 
        || '';
      // Normalize: handle both snake_case keys and possible translated strings
      const normalizedKey = (originalKey || '').toLowerCase().replace(/[\s-]/g, '_');
      if (normalizedKey === 'email_not_verified' || normalizedKey.includes('not_verified')) {
        return { success: false, errorKey: 'email_not_verified' };
      }
      if (normalizedKey === 'account_deactivated' || normalizedKey.includes('deactivated')) {
        return { success: false, errorKey: 'account_deactivated' };
      }
      return { success: false, errorKey: 'invalid_credentials' };
    }
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    
    // Check if email verification is required (normal flow)
    if (response.data.email_verification_required) {
      return {
        email_verification_required: true,
        email: response.data.email,
        message: response.data.message,
        verification_token: response.data.verification_token,
        verification_url: response.data.verification_url
      };
    }
    
    // Super admin flow: token is returned immediately
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user && !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
